import { Component, OnInit, HostBinding, ViewChild, ElementRef, OnDestroy, Inject } from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { Router, ActivatedRoute } from '@angular/router';

import { of, Observable, Subscription, forkJoin } from 'rxjs';
import { tap, map, take } from 'rxjs/operators';

import { GridComponent, GridDataResult, DataStateChangeEvent, RowClassArgs, FilterService } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { ExcelExportEvent } from '@progress/kendo-angular-grid/dist/es2015/excel/excel-export-event';

import { toDataURL, ExcelExportData } from '@progress/kendo-angular-excel-export';
import { saveAs } from '@progress/kendo-file-saver';

import * as $ from 'jquery';
import * as moment from 'moment';

import { AppNotificationsService, routerTransition } from '@libs/portal-common/system';
import { QualityAnalysisFilter, QualityAnalysisService } from '../services/quality-analysis.service';
import {
  ContactsService,
  AccessManagementService,
  IncidentQualityAnalysisQuestion,
  IncidentsQualityAnalysisQuestionService,
  AnswerType,
  ANALYTICS,
  IAnalyticsService,
  AnalyticsEvent,
  ActivityReportName,
  IIncidentQualityAnalysis,
  IncidentsQualityAnalysisTemplateService,
  IncidentQualityAnalysisTemplate,
  LocationsService,
} from '@libs/portal-common/services';
import { GridStateManagerService, GridStateNormalizers, IGridStateManagerConfig, IGridWithState } from '@libs/portal-common/shared';

class IncidentQualityAnalysisQuestionGroup extends IncidentQualityAnalysisQuestion {
  deleted: Array<IncidentQualityAnalysisQuestion>;
}

interface IGridState extends IGridWithState<QualityAnalysisFilter> {
  filter: QualityAnalysisFilter;
  state: State;
}

const DEFAULT_STATE: State = {
  skip: 0,
  take: 30,
  group: [],
  filter: { logic: 'and', filters: [] },
  sort: [{ field: 'fkIncident', dir: 'desc' }],
};

@Component({
  selector: 'app-quality-analysis',
  templateUrl: './quality-analysis.component.html',
  styleUrls: ['./quality-analysis.component.scss'],
  animations: [routerTransition],
})
export class QualityAnalysisComponent implements OnInit, OnDestroy {
  @ViewChild(GridComponent, { static: true }) private grid: GridComponent;
  @HostBinding('@routerTransition') routerTransition = '';
  @ViewChild('qaGridContainer', { static: true }) private gridContainer: ElementRef;
  @ViewChild('answerModal', { static: true }) public answerModal: ModalDirective;

  filter = new QualityAnalysisFilter();

  filterLoading = false;
  private gridStateManager: GridStateManagerService<IGridState>;

  contacts = [];
  filteredContacts = [];
  contactsEnabled = false;

  locations = [];
  filteredLocations = [];
  locationsEnabled = false;

  templates: Array<IncidentQualityAnalysisTemplate> = [];

  questions: Array<IncidentQualityAnalysisQuestionGroup> = [];

  filteredQuestions: Array<IncidentQualityAnalysisQuestionGroup> = [];

  originUrl = window.location.origin;

  state: State = DEFAULT_STATE;

  view: Observable<GridDataResult>;

  excelFileName = 'QualityAnalysis.xlsx';
  cells: any = {
    textAlign: 'right',
  };

  answerModalTitle = '';
  answerModalText = '';
  private longAnswerThreshold = 60;

  private selectedContactValues: any[] = [];
  public get selectedContact(): any[] {
    return this.selectedContactValues;
  }

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private notifications: AppNotificationsService,
    private contactsService: ContactsService,
    private locationsService: LocationsService,
    private accessManagementService: AccessManagementService,
    private questionService: IncidentsQualityAnalysisQuestionService,
    private templateService: IncidentsQualityAnalysisTemplateService,
    private service: QualityAnalysisService,
    @Inject(ANALYTICS) private analytcis: IAnalyticsService,
  ) {
    this.view = service;

    this.view.subscribe((x) => {
      this.resizeGrid();
    });
  }

  ngOnInit() {
    this.analytcis.track(AnalyticsEvent.DailyActivityOpen, { ReportName: ActivityReportName.QualityAnalysis });
    this.initializeGridStateManager();

    this.questionService.getAllPost(true).subscribe((response) => {
      this.questions = this.groupQuestions(response.Data);
    });

    this.filterLoading = true;
    let common = of([]);
    if (this.accessManagementService.hasAccessPermissions({ entities: ['Locations'], access: 'CanRead' })) {
      common = this.contactsService.getAll().pipe(
        tap((response) => {
          this.contacts = (response || []).map(function (contact) {
            return {
              Id: contact.Id,
              SIP: contact.SipAddress,
              Name: contact.Name,
            };
          });

          this.filteredContacts = this.contacts;
          this.contactsEnabled = true;
        }),
      );
    } else {
      this.contactsEnabled = false;
    }

    let locations = of([]);
    if (this.accessManagementService.hasAccessPermissions({ entities: ['Locations'], access: 'CanRead' })) {
      locations = this.locationsService.getAll().pipe(
        tap((response) => {
          this.locations = (response.Data || []).map(function (location) {
            return {
              Id: location.Id,
              LotName: `${location.LotNumber} ${location.LotName}`,
            };
          });

          this.filteredLocations = this.locations;
          this.locationsEnabled = true;
        }),
      );
    } else {
      this.locationsEnabled = false;
    }

    // Load templates
    const templatesRequest = this.templateService.getAllAssigned().pipe(
      tap((response) => {
        this.templates = response.Data || [];
      }),
    );

    forkJoin([common, locations, templatesRequest]).subscribe(() => {
      this.gridStateManager.loadState();
      this.filterLoading = false;
      this.queryData();
    });
  }

  ngOnDestroy(): void {
    if (this.gridStateManager) {
      this.gridStateManager.destroy();
    }
  }

  search() {
    this.gridStateManager.saveState();
    this.queryData();
  }

  private queryData() {
    this.service.query(this.filter, this.state);
    this.updateFilteredQuestions();
  }

  handleContactsFilter(value) {
    this.filteredContacts = this.contacts.filter((s) => s.Name.toLowerCase().indexOf(value.toLowerCase()) !== -1);
  }

  handleLocationsFilter(value) {
    this.filteredLocations = this.locations.filter((s) => s.LotName.toLowerCase().indexOf(value.toLowerCase()) !== -1);
  }

  dataStateChange(state: DataStateChangeEvent): void {
    state.sort = state.sort.filter((item) => !state.group.find((gr) => gr.field === item.field));

    this.state = state;
    this.gridStateManager.saveState();
    this.service.query(this.filter, this.state);
  }

  rowSelectionChange($event: any) {
    const dataItem = $event.selectedRows[0].dataItem;
    this.router.navigate([dataItem.fkIncident, 'quality'], { relativeTo: this.activatedRoute });
  }

  rowCallback(context: RowClassArgs) {
    return {
      incident: true,
    };
  }

  creatorName(contactId: number): string {
    let creator = this.contacts.find((x) => x.Id === contactId);
    return !!creator ? creator.Name : null;
  }

  parseArray(answerArray: string): string {
    if (answerArray) {
      return JSON.parse(answerArray).join(', ');
    } else {
      return answerArray;
    }
  }

  public getAnswerText(question: IncidentQualityAnalysisQuestionGroup, item: IIncidentQualityAnalysis): string | null {
    const questionAnswer = this.getQuestionAnswer(question, item);

    if (this.getString(question.AnswerType) === 'MultiChoice') {
      return this.parseArray(questionAnswer);
    }

    return questionAnswer;
  }

  public isLongStringAnswer(question: IncidentQualityAnalysisQuestionGroup, item: IIncidentQualityAnalysis): boolean {
    const answer = this.getQuestionAnswer(question, item);
    return this.getString(question.AnswerType) === 'String' && answer?.length > this.longAnswerThreshold;
  }

  public openAnswerModal(question: IncidentQualityAnalysisQuestionGroup, item: IIncidentQualityAnalysis, event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const answer = this.getQuestionAnswer(question, item);
    this.answerModalTitle = question?.QuestionName ?? 'Answer';
    this.answerModalText = answer ?? '';
    this.answerModal.show();
  }

  public closeAnswerModal(): void {
    this.answerModal.hide();
  }

  excelExport(event: ExcelExportEvent) {
    event.preventDefault();
    this.analytcis.track(AnalyticsEvent.DailyActivityExport, { ReportName: ActivityReportName.QualityAnalysis, ExportType: 'Excel' });

    let sheet = event.workbook.sheets[0];
    let columns = sheet.columns;
    let header = sheet.rows[0];

    sheet.rows.splice(1, sheet.rows.length);

    let state = JSON.parse(JSON.stringify(this.state));
    state.skip = 0;
    state.take = 0;

    // set columns width to auto
    columns.forEach((column) => {
      delete column.width;
      column.autoWidth = true;
      column.autoHeight = true;
    });

    this.service.getQA(this.filter, state).subscribe((res) => {
      // quality Analysis List
      const qA = res.data;
      const questionIds: any = {};

      // push question Names to header cells
      this.filteredQuestions.forEach((question) => {
        questionIds[question.Id] = question;
        header.cells.push({
          background: '#7a7a7a',
          color: '#fff',
          type: 'header',
          value: question.QuestionName,
        });
        columns.push({
          autoWidth: true,
          autoHeight: true,
        });
      });

      let localDateTimeFormat = moment.localeData().longDateFormat('LLL');
      if (localDateTimeFormat.endsWith(' A')) {
        localDateTimeFormat += 'M/PM';
      }

      for (let index = 0; index < qA.length; index++) {
        let row: { type: string; cells: Array<any>; height: number } = { type: 'data', cells: [], height: 20 };

        let maxLength = -1;

        row.cells.push({ value: qA[index].fkIncident, textAligin: 'right' });
        row.cells.push({ value: this.creatorName(qA[index].fkAgent), textAligin: 'right' });
        row.cells.push({ value: new Date(qA[index].Created), textAligin: 'right' });

        this.filteredQuestions.forEach((question) => {
          let answer: any = this.getQuestionAnswer(question, qA[index]);

          if (this.getString(question.AnswerType) === 'MultiChoice' && answer) {
            const strArray = JSON.parse(answer);
            if (strArray.length > maxLength) {
              maxLength = strArray.length;
              row.height += (maxLength - 1) * 20;
            }
            answer = strArray.join('\n');
          }
          row.cells.push({
            wrap: true,
            value: answer,
            textAlign: 'right',
          });

          if (answer === 'No') {
            row.cells[row.cells.length - 1].color = '#ff0000'; // red
          } else if (answer === 'Yes') {
            row.cells[row.cells.length - 1].color = '#008000'; // green
          }
        });

        sheet.rows.push(row);
      }

      let dataURL = toDataURL(event.workbook);
      Promise.resolve(dataURL).then((data) => {
        saveAs(data, this.excelFileName);
        this.notifications.success('Your export is ready! The file will download automatically.');
      });
    });
  }

  fetchData(grid: GridComponent): Promise<ExcelExportData> {
    let state = JSON.parse(JSON.stringify(this.state));
    state.skip = 0;
    state.take = 0;

    return this.service
      .getQA(this.filter, state)
      .pipe(
        map((res) => {
          return {
            data: res.data,
            group: grid.group,
          };
        }),
      )
      .toPromise();
  }

  private resizeGrid() {
    if (!this.grid) {
      return;
    }

    let page = $('.page-host'),
      pageHeight = page.innerHeight();

    let header = $('.page__heading'),
      headerHeight = header.innerHeight();

    let gridContainer = $('.grid-container'),
      gridContainerHeight = gridContainer.innerHeight();

    let limit = 30;

    let actualPageHeight = headerHeight + gridContainerHeight + 20;

    let delta = pageHeight - actualPageHeight;

    if (Math.abs(delta) > limit) {
      let $grid = $(this.grid.wrapper.nativeElement);
      let gridHeight = $grid.innerHeight();

      let newHeight = gridHeight + delta;
      $grid.css('height', newHeight + 'px');
    }
  }

  private getString(answerType: AnswerType) {
    return answerType + '';
  }

  private groupQuestions(questions: IncidentQualityAnalysisQuestion[]): IncidentQualityAnalysisQuestionGroup[] {
    const result: Array<IncidentQualityAnalysisQuestionGroup> = [];
    const groups = {};

    questions = questions.sort((a, b) => {
      const nameA = a.QuestionName.trim();
      const nameB = b.QuestionName.trim();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      return 0;
    });

    questions
      .filter((x) => !x.IsDeleted)
      .forEach((x) => {
        const item = x as IncidentQualityAnalysisQuestionGroup;
        item.deleted = [];
        result.push(item);
        groups[item.QuestionName.trim()] = item;
      });

    questions
      .filter((x) => x.IsDeleted)
      .forEach((x) => {
        const group = groups[x.QuestionName.trim()] as IncidentQualityAnalysisQuestionGroup;

        if (group) {
          group.deleted.push(x);
        } else {
          const item = x as IncidentQualityAnalysisQuestionGroup;
          item.deleted = [];
          result.push(item);
          groups[item.QuestionName] = item;
        }
      });

    return result;
  }

  public getQuestionAnswer(question: IncidentQualityAnalysisQuestionGroup, item: IIncidentQualityAnalysis): string | null | undefined {
    let result: string | null | undefined = item.IncidentQualityAnalysisAnswersDict[question.Id];

    if (!result) {
      return question.deleted.map((x) => item.IncidentQualityAnalysisAnswersDict[x.Id]).find((x) => !!x);
    }

    return result;
  }

  public multiSelectValueChange(values: any[], columnField: string, filterService: FilterService): void {
    this.selectedContactValues = values;

    filterService.filter({
      filters: values.map((value) => ({
        field: columnField,
        operator: 'eq',
        value,
      })),
      logic: 'or',
    });
  }

  private initializeGridStateManager(): void {
    this.gridStateManager = new GridStateManagerService<IGridState>(this.router, this.activatedRoute);

    const config: IGridStateManagerConfig<IGridState> = {
      storageKey: `ACTIVITY_FILTER_STATE_QUALITY_ANALYSIS`,
      normalizeState: GridStateNormalizers.createGridWithFilterNormalizer(new QualityAnalysisFilter()),
      getCurrentState: () => ({ filter: this.filter, state: this.state }),
      applyState: (state) => {
        this.state = state.state;
        this.filter = state.filter;
      },
    };

    this.gridStateManager.initialize(config);
  }

  resetFilter() {
    this.filter = new QualityAnalysisFilter();
    this.state = DEFAULT_STATE;
    this.gridStateManager.saveState();
    this.updateFilteredQuestions();
    this.search();
  }

  public updateFilteredQuestions(): void {
    if (!this.filter.TemplateId) {
      this.filteredQuestions = this.questions.filter((q) => !q.IsSystem);
      return;
    }

    const selectedTemplate = this.templates.find((t) => t.Id === this.filter.TemplateId);
    if (selectedTemplate && selectedTemplate.Questions) {
      const sortedQuestions: Array<IncidentQualityAnalysisQuestionGroup> = [];
      selectedTemplate.Questions.forEach((templateQuestion) => {
        const question = this.questions.find((q) => q.Id === templateQuestion.Id);

        if (question) {
          sortedQuestions.push(question);
        } else {
          const deletedQuestion = this.questions.find((q) => q.deleted.some((dq) => dq.Id === templateQuestion.Id));

          if (deletedQuestion) {
            sortedQuestions.push(deletedQuestion);
          }
        }
      });

      this.filteredQuestions = sortedQuestions.filter((q) => !q.IsSystem);
    } else {
      this.filteredQuestions = this.questions.filter((q) => !q.IsSystem);
    }
  }

  public trackByQuestion(index: number, question: IncidentQualityAnalysisQuestionGroup): number {
    return question.Id;
  }
}
